import React, { useMemo, useState, useEffect } from "react";
import { ChevronDown, ChevronRight } from "lucide-react";
import { Switch } from "antd";
import CustomButton from "../../../components/Elements/Buttons/CustomButtons";
import { putApi } from "../../../api/api_instance";
import { API_URLS } from "../../../api/apiUrls";
import { getOptionLabel } from "../../AddQuestionBank/components/questionOptions";
import QuestionInput from "../../AddQuestionBank/components/QuestionInput";
import QuestionTable from "../../AddQuestionBank/components/questionTable";
import PopupCard from "../../../components/Cards/PopupCard/PopupCard";
import {
  SUFFIX_SEP,
  TRIP_SEP,
  GuidanceBlock,
  evalCondition,
  getMultiselectDependency,
} from "../../AddQuestionBank/components/displayConditionUtils";

const QuestionCard = ({
  question,
  displayIndex,
  answers,
  setAnswers,
  handleAnswerChange,
  questionsList,
  optionsCache,
  setOptionsCache,
  tripIndex = null,
  optionSuffix = "",
  optionLabel = "",
}) => {
  let id = question.questionId;
  if (tripIndex !== null) id += `${TRIP_SEP}${tripIndex}`;
  if (optionSuffix) id += `_${optionSuffix}`;

  return (
    <div className="rounded-[10px] border border-[#D9E1E7] bg-white p-4">
      <GuidanceBlock guidance={question.guidance} />

      <div className="flex justify-between items-start gap-4 mb-3">
        <div className="flex items-start flex-1">
          <span className="text-[15px] font-semibold text-[#1F2937] leading-6">
            {displayIndex}. {question.questionText}
          </span>
        </div>
      </div>

      {(tripIndex !== null || optionLabel) && (
        <div className="mb-3 flex flex-wrap gap-2">
          {tripIndex !== null && (
            <span className="inline-flex items-center rounded-full bg-[#D7F3EC] px-3 py-1 text-[12px] font-medium text-[#0F766E]">
              Trip {tripIndex + 1}
            </span>
          )}

          {optionLabel && (
            <span className="inline-flex items-center rounded-full bg-[#EEF2FF] px-3 py-1 text-[12px] font-medium text-[#4338CA]">
              {optionLabel}
            </span>
          )}
        </div>
      )}

      {question.questionType === "table" ? (
        <QuestionTable
          question={question}
          tableId={id}
          answers={answers}
          setAnswers={setAnswers}
        />
      ) : (
        <QuestionInput
          field={question}
          value={answers[id]}
          onChange={(val) => handleAnswerChange(id, val)}
          onOptionsLoaded={(qid, opts) => {
            setOptionsCache((prev) => ({ ...prev, [qid]: opts }));
          }}
        />
      )}
    </div>
  );
};

const Division = ({
  divisionName,
  questions,
  answers,
  setAnswers,
  handleAnswerChange,
  questionsList,
  optionsCache,
  setOptionsCache,
}) => {
  const [open, setOpen] = useState(true);

  const renderChildren = (
    parentId,
    depth,
    tripIndex,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath
  ) => {
    return questionsList
      .filter((child) => {
        if (!child.displayCondition) return false;

        const parentConditions = child.displayCondition.filter(
          (c) => c.triggerQuestionId === parentId
        );
        if (parentConditions.length === 0) return false;

        const isSatisfied = (c) => {
          if (repeatingParentId && c.triggerQuestionId === parentId)
            return true;
          return evalCondition(c, answers, {
            tripIndex,
            optionSuffix,
            repeatingParentId,
          });
        };

        const logicalOp = (child.logicalOperator || "AND").toUpperCase();
        if (logicalOp === "OR") return parentConditions.some(isSatisfied);
        return parentConditions.some(isSatisfied);
      })
      .map((child, idx) => {
        let childSuffix = optionSuffix;
        if (child.displayCondition) {
          const specificCond = child.displayCondition.find(
            (c) =>
              c.triggerQuestionId === parentId &&
              c.operator === "eq" &&
              typeof c.value === "string"
          );
          if (specificCond) {
            childSuffix = optionSuffix
              ? `${optionSuffix}${SUFFIX_SEP}${specificCond.value}`
              : specificCond.value;
          }
        }

        return renderNode(
          child,
          depth,
          tripIndex,
          childSuffix,
          repeatingParentId,
          `${indexPath}.${idx + 1}`
        );
      });
  };

  const renderNode = (
    question,
    depth = 0,
    tripIndex = null,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath = "1"
  ) => {
    const isVisible = evalCondition(question, answers, {
      tripIndex,
      optionSuffix,
      repeatingParentId,
    });
    if (!isVisible) return null;

    const multiselectTriggerId = getMultiselectDependency(
      question,
      answers,
      questionsList,
      tripIndex,
      optionSuffix
    );

    if (multiselectTriggerId) {
      let items;
      if (answers[multiselectTriggerId]) items = answers[multiselectTriggerId];
      if (!items) {
        let s = optionSuffix;
        while (!items) {
          if (tripIndex !== null && s)
            items = answers[`${multiselectTriggerId}${TRIP_SEP}${tripIndex}_${s}`];
          if (!items && s) items = answers[`${multiselectTriggerId}_${s}`];
          if (!items && tripIndex !== null)
            items = answers[`${multiselectTriggerId}${TRIP_SEP}${tripIndex}`];
          if (!items) items = answers[multiselectTriggerId];

          if (items || !s || !s.includes(SUFFIX_SEP)) break;
          s = s.substring(0, s.lastIndexOf(SUFFIX_SEP));
        }
      }
      const actualSelectedItems = (items || []).filter((v) => v !== "");

      if (actualSelectedItems.length > 0) {
        return (
          <div key={`ms_${question.questionId}_${optionSuffix}`} className="space-y-4">
            {actualSelectedItems.map((itemValue, idx) => {
              const optionLabel = getOptionLabel(multiselectTriggerId, itemValue, optionsCache);

              return renderInstance(
                question,
                depth,
                tripIndex,
                optionSuffix
                  ? `${optionSuffix}${SUFFIX_SEP}${itemValue}`
                  : itemValue,
                repeatingParentId,
                `${indexPath}.${idx + 1}`,
                optionLabel
              );
            })}
          </div>
        );
      }
    }

    return renderInstance(
      question,
      depth,
      tripIndex,
      optionSuffix,
      repeatingParentId,
      indexPath
    );
  };

  const renderInstance = (
    question,
    depth,
    tripIndex,
    optionSuffix = null,
    repeatingParentId = null,
    indexPath,
    optionLabel = ""
  ) => {
    const originalQid = question.questionId;
    let qid = originalQid;
    if (tripIndex !== null) qid += `${TRIP_SEP}${tripIndex}`;
    if (optionSuffix) qid += `_${optionSuffix}`;

    return (
      <div
        key={qid}
        className={`space-y-4 ${
          depth > 0 ? "ml-6 mt-3 border-l-4 border-l-teal-500 pl-4" : "mb-6"
        }`}
      >
        <QuestionCard
          question={question}
          displayIndex={indexPath}
          answers={answers}
          setAnswers={setAnswers}
          handleAnswerChange={handleAnswerChange}
          questionsList={questionsList}
          optionsCache={optionsCache}
          setOptionsCache={setOptionsCache}
          tripIndex={tripIndex}
          optionSuffix={optionSuffix}
          optionLabel={optionLabel}
        />

        {/* --- GENERIC NUMBER REPETITION LOOP --- */}
        {(question.questionType === "number" ||
          originalQid === "number_of_trip_select") &&
        parseInt(answers[qid]) > 0 &&
        questionsList.some((child) =>
          child.displayCondition?.some(
            (c) => c.triggerQuestionId === originalQid
          )
        ) ? (
          <div className="mt-6 space-y-6">
            {Array.from(
              { length: Math.min(parseInt(answers[qid]), 100) },
              (_, i) => (
                <div
                  key={`${qid}_loop_${i}`}
                  className="p-4 border-2 border-teal-100 rounded-xl bg-teal-50/10"
                >
                  <div className="text-teal-600 font-bold mb-4 uppercase text-sm tracking-wider">
                    {originalQid.includes("trip") ||
                    originalQid.includes("journey")
                      ? "Trip"
                      : "Entry"}{" "}
                    {i + 1}
                  </div>
                  {renderChildren(
                    originalQid,
                    depth + 1,
                    i,
                    optionSuffix,
                    originalQid,
                    indexPath
                  )}
                </div>
              )
            )}
          </div>
        ) : (
          renderChildren(
            originalQid,
            depth + 1,
            tripIndex,
            optionSuffix,
            repeatingParentId,
            indexPath
          )
        )}
      </div>
    );
  };

  const rootQuestions = questions.filter(
    (q) => !q?.displayCondition || q.displayCondition.length === 0
  );

  return (
    <div className="mb-4 border-b pb-3">
      <div
        className="flex justify-between items-center cursor-pointer mb-3"
        onClick={() => setOpen(!open)}
      >
        <h2 className="text-[18px] font-bold text-[#06513D] flex items-center">
          {open ? (
            <ChevronDown size={18} className="mr-2" />
          ) : (
            <ChevronRight size={18} className="mr-2" />
          )}
          {divisionName}
        </h2>
      </div>

      {open && (
        <div className="space-y-4">
          {rootQuestions.map((question, index) =>
            renderNode(question, 0, null, null, null, `${index + 1}`)
          )}
        </div>
      )}
    </div>
  );
};

export const QuestionForm = ({ questions }) => {
  const [answers, setAnswers] = useState({});
  const [optionsCache, setOptionsCache] = useState({});

  const questionsList = useMemo(() => {
    if (!questions) return [];
    let flat = [];
    questions.forEach((item) => {
      if (item?.questions && Array.isArray(item.questions)) {
        flat.push(...item.questions);
      } else if (item?.questionId) {
        flat.push(item);
      }
    });

    const uniqueMap = new Map();
    flat.forEach((q) => {
      const existing = uniqueMap.get(q.questionId);
      if (!existing) {
        uniqueMap.set(q.questionId, q);
      } else {
        if (
          (!existing.displayCondition || existing.displayCondition.length === 0) &&
          q.displayCondition &&
          q.displayCondition.length > 0
        ) {
          uniqueMap.set(q.questionId, q);
        }
      }
    });
    return Array.from(uniqueMap.values());
  }, [questions]);

  const handleAnswerChange = (questionId, value) => {
    setAnswers((prev) => ({ ...prev, [questionId]: value }));
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {questions.map((division) => (
        <Division
          key={division.divisionId}
          divisionName={division.divisionTitle}
          questions={division.questions || []}
          answers={answers}
          setAnswers={setAnswers}
          handleAnswerChange={handleAnswerChange}
          questionsList={questionsList}
          optionsCache={optionsCache}
          setOptionsCache={setOptionsCache}
        />
      ))}
    </div>
  );
};
